/* eslint-disable max-lines-per-function */
import {
  TaskWithColor,
  useCalendarStore
} from '@components/calendar/calendar-store'
import {cn} from '@containers/html-utils'
import {globals} from '@extensions/globals-base'
import {UserIcon} from '@extensions/sticker/views-web/user/user-sticker-state'
import {endOfDay} from 'date-fns'
import {observer} from 'mobx-react-lite'
import React from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {DataObjectId, TaskData} from 'yougile-model'
import {Time} from 'yougile-util'
import {
  capitalizeDateFormat,
  lightenColor,
  minTaskHeight,
  validDateFormat
} from '../../utils'
import {getMaxHeight} from '../all-day/all-day-utils'
import {useDraggableStore} from '../draggable/draggable-store'
import {ShowDraggedTime} from '../hour/hour'
import IconDone from './icon-done.svg'
import IconOverdue from './icon-overdue.svg'
import IconSubtask from './icon-subtask.svg'
import {TimeTw} from './task-content'

const DoneIconTw = tw.div`
  w-10
  h-10
  mr-4
  mt-4
  flex
  text-success-old
  float-left
`

const OverdueIconTw = tw.div`
  w-12
  h-12
  mr-4
  mt-2
  flex
  text-error-old
  float-left
`

const SubtaskIconTw = tw.div`
  float-left
  w-16
  h-16
  text-third-old
`

const TwTaskText = tw.div<{
  $notAllDayWithCustomTop?: boolean
  $notAllDay?: boolean
  $constantHeight?: boolean
}>`
  flex
  overflow-hidden
  max-h-full
  w-full
  ${p => (p.$notAllDayWithCustomTop ? 'sticky mb-24' : '')}
  ${p => (p.$notAllDay ? 'sticky mb-8' : '')}
  ${p => (p.$constantHeight ? 'h-28' : '')}
`

const TwTaskTextInner = tw.div<{$notMinHeight?: boolean}>`
  ${p =>
    p.$notMinHeight
      ? 'w-full'
      : `
      truncate
      flex
      w-full
      justify-between
      self-center
  `}
`

const headerDaysHeight = 40
const calendarMarginTop = 16

const getDoneIcon = () => {
  return (
    <DoneIconTw>
      <IconDone />
    </DoneIconTw>
  )
}

const getOverdueIcon = () => {
  return (
    <OverdueIconTw>
      <IconOverdue />
    </OverdueIconTw>
  )
}

const getSubtaskIcon = () => {
  return (
    <SubtaskIconTw>
      <IconSubtask />
    </SubtaskIconTw>
  )
}

export const TaskText = observer(
  ({
    allDay,
    height,
    taskId,
    hideDeadline,
    realStartDate,
    realDeadline,
    gtdStage,
    roundedDeadline,
    columnColor,
    title,
    isColorDark,
    textOpacity,
    assigned,
    customTop,
    isSubtask,
    timeWithDate = false
  }: {
    allDay: boolean
    height: number
    hideDeadline: boolean
    taskId: DataObjectId
    realStartDate: TaskData['startDate']
    realDeadline: TaskData['deadline']
    gtdStage: TaskData['gtdStage']
    roundedDeadline: TaskWithColor['roundedDeadline']
    columnColor: TaskWithColor['columnColor']
    title: TaskData['title']
    isColorDark: TaskWithColor['isColorDark']
    textOpacity?: string
    assigned: TaskData['assigned']
    isSubtask: TaskWithColor['isSubtask']
    customTop?: number
    timeWithDate?: boolean
  }) => {
    const [draggableState] = useDraggableStore()
    const [state] = useCalendarStore()
    const language = Lang.getLang()
    const maxHeight = getMaxHeight(state.showMoreAllDayTasks)
    const heightTop =
      maxHeight > state.allDayTasksHeight ? state.allDayTasksHeight : maxHeight
    const marginTop = heightTop + calendarMarginTop
    const headerTop = heightTop + headerDaysHeight

    return (
      <TwTaskText
        $constantHeight={height <= minTaskHeight}
        $notAllDay={!allDay}
        $notAllDayWithCustomTop={Boolean(!allDay && customTop)}
        style={{
          paddingTop: customTop && Math.abs(customTop),
          top:
            (!allDay && customTop && marginTop) ||
            (!allDay && headerTop) ||
            undefined
        }}
      >
        <TwTaskTextInner $notMinHeight={height > minTaskHeight}>
          <div
            className={cn(
              'flex',
              height > minTaskHeight ? 'flex-col w-full' : 'truncate'
            )}
          >
            {height > minTaskHeight && (
              <div className="flex justify-between h-20 mt-4">
                {draggableState.draggedItem.id &&
                draggableState.draggedItem.id === taskId &&
                draggableState.draggedItem.changeAction ===
                  'change-deadline' ? (
                  <ShowDraggedTime />
                ) : (
                  <TimeTw>
                    {timeWithDate
                      ? capitalizeDateFormat(
                          Time.toLocalDate(+realStartDate),
                          language,
                          'd MMM HH:mm'
                        )
                      : validDateFormat(
                          Time.toLocalDate(+realStartDate),
                          'HH:mm'
                        )}
                    {!hideDeadline && (
                      <>
                        -
                        {validDateFormat(
                          Time.toLocalDate(+realDeadline),
                          'HH:mm'
                        )}
                      </>
                    )}
                  </TimeTw>
                )}
                <Users assigned={assigned} height={height} />
              </div>
            )}
            <div
              className={cn(
                'z-10 flex',
                height <= minTaskHeight && 'self-center truncate sticky'
              )}
            >
              <div
                className={cn(
                  'break-words leading-4 overflow-hidden font-semibold text-14 mt-1',
                  height <= minTaskHeight && 'truncate',
                  textOpacity && textOpacity
                )}
                style={{
                  color: lightenColor(
                    columnColor,
                    isColorDark ? 50 : globals.theme === 'dark' ? 35 : -35
                  )
                }}
              >
                {gtdStage === 'done'
                  ? getDoneIcon()
                  : allDay
                  ? endOfDay(Time.toLocalDate(+realDeadline)) <
                      state.currentTime && getOverdueIcon()
                  : roundedDeadline < state.currentTime && getOverdueIcon()}
                {isSubtask && getSubtaskIcon()}
                {title}
              </div>
            </div>
          </div>
          {height <= minTaskHeight && (
            <Users assigned={assigned} height={height} />
          )}
        </TwTaskTextInner>
      </TwTaskText>
    )
  }
)

const Users = ({
  assigned,
  height = 0
}: {
  assigned: TaskData['assigned']
  height?: number
}) => {
  if (!assigned?.length) {
    return null
  }

  return (
    <div
      className={cn(
        'flex shrink-0 ml-16',
        height <= minTaskHeight ? 'self-center mt-2' : 'float-right'
      )}
    >
      {assigned.slice(0, 3).map((userId, ix) => (
        <UserIcon
          key={userId}
          userId={userId}
          shift={ix === 0 ? 13 : 16}
          hideTitle={true}
          zIndex={100 - ix}
          size={20}
        />
      ))}
    </div>
  )
}
